import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Calculator, Shield, AlertTriangle } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-md shadow-sm border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-600 rounded-xl flex items-center justify-center">
                <Calculator className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
                Tax Helper Pros
              </span>
            </div>
            
            <Button 
              variant="ghost" 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-6">
            <Shield className="w-12 h-12 text-blue-600" />
            <h1 className="text-4xl font-bold text-slate-800" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
              Terms of Service
            </h1>
          </div>
          <p className="text-lg text-slate-600">
            Last Updated: September 27, 2024
          </p>
        </div>

        <Card className="shadow-xl border-0 mb-8">
          <CardHeader className="bg-orange-50 border-b">
            <CardTitle className="flex items-center gap-3 text-orange-800">
              <AlertTriangle className="w-6 h-6" />
              Important Notice
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 bg-orange-50">
            <div className="space-y-3 text-orange-800 font-medium">
              <p>• Tax Helper Pros is a tool, not a tax professional.</p>
              <p>• Always have a licensed or experienced tax professional review your records before filing.</p>
              <p>• We're not liable for IRS penalties, errors, or omissions resulting from unverified use of the Service.</p>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardContent className="p-8">
            <div className="prose prose-slate max-w-none" style={{fontFamily: 'Inter, sans-serif'}}>
              <p className="text-lg text-slate-700 mb-8">
                Welcome to Tax Helper Pros ("we," "our," "us"). These Terms of Service ("Terms") govern your access to and use of the Tax Helper Pros application, website, and related services (collectively, the "Service"). By using the Service, you agree to these Terms. If you do not agree, you may not use the Service.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">1. Purpose of the Service</h2>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Tax Helper Pros is designed to assist users in organizing and analyzing financial information, such as bank statements, to support tax preparation.</li>
                <li>We do not provide tax, legal, or financial advice. The Service is intended as a tool for informational and organizational purposes only.</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">2. Not a Substitute for Professional Advice</h2>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>Tax Helper Pros does not replace the services of a certified public accountant (CPA), enrolled agent, attorney, or any other licensed tax professional.</li>
                <li>Any data, reports, or summaries generated by the Service are for assistance only.</li>
                <li>Before submitting any tax records, filings, or information to the Internal Revenue Service (IRS) or other tax authorities, you must review and verify the information with a licensed or experienced tax professional.</li>
                <li>We are not responsible for errors, omissions, or penalties arising from your use of the Service or from the filing of inaccurate or incomplete tax returns.</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">3. Eligibility</h2>
              <p className="text-slate-700">
                You must be at least 18 years old and legally capable of entering into a binding agreement to use the Service.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">4. User Responsibilities</h2>
              <ul className="list-disc pl-6 space-y-2 text-slate-700">
                <li>You are solely responsible for the accuracy, completeness, and legality of any information you upload or input into the Service.</li>
                <li>You agree not to use the Service for any unlawful purpose or to violate any applicable tax, financial, or privacy laws.</li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">5. No Guarantee of Outcomes</h2>
              <p className="text-slate-700">
                We do not guarantee that use of the Service will result in any particular tax savings, refund amount, or compliance outcome. Your results may vary depending on the accuracy and completeness of your data and the quality of the professional review you obtain.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">6. Data Security and Privacy</h2>
              <p className="text-slate-700">
                We take reasonable measures to protect your information; however, we cannot guarantee absolute security. By using the Service, you consent to our collection and processing of your data as described in our Privacy Policy. All data is encrypted, secure, and deleted after processing. We DO NOT store your data.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">7. Intellectual Property</h2>
              <p className="text-slate-700">
                All content, features, and functionality of the Service—including but not limited to software, text, graphics, logos, and trademarks—are owned by or licensed to Tax Helper Pros and protected by intellectual property laws.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">8. Termination of Access</h2>
              <p className="text-slate-700">
                We may suspend or terminate your access to the Service at our discretion, without notice, if you violate these Terms or misuse the Service.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">9. Limitation of Liability</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 my-4">
                <p className="text-red-800 font-medium mb-2">To the fullest extent permitted by law:</p>
                <ul className="list-disc pl-6 space-y-2 text-red-800">
                  <li>Tax Helper Pros and its affiliates, officers, and employees are not liable for any indirect, incidental, special, consequential, or punitive damages arising out of or relating to your use of the Service.</li>
                  <li>Our total liability to you for any claim arising out of or relating to the Service is limited to the amount you paid for the Service in the 12 months preceding the claim.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">10. Indemnification</h2>
              <p className="text-slate-700">
                You agree to defend, indemnify, and hold harmless Tax Helper Pros, its officers, employees, and affiliates from any claims, damages, liabilities, and expenses (including attorney's fees) arising from your use of the Service or your violation of these Terms.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">11. Changes to These Terms</h2>
              <p className="text-slate-700">
                We may update these Terms from time to time. If we make material changes, we will provide notice by updating the "Last Updated" date above or by other reasonable means. Your continued use of the Service after changes constitutes acceptance of the revised Terms.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">12. Governing Law</h2>
              <p className="text-slate-700">
                These Terms are governed by and construed in accordance with the laws of the United States, without regard to its conflict of law provisions.
              </p>

              <h2 className="text-2xl font-bold text-slate-800 mt-8 mb-4">13. Contact Us</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-blue-800 mb-2">If you have questions about these Terms, please contact us at:</p>
                <p className="text-blue-800 font-medium">support@taxhelperpros.com</p>
                <p className="text-blue-800">Tax Helper Pros LLC</p>
                <p className="text-blue-800">United States</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-center gap-4 mt-8">
          <Button 
            onClick={() => navigate('/')}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            Back to Home
          </Button>
          <Button 
            variant="outline"
            onClick={() => navigate('/auth')}
            className="border-blue-600 text-blue-600 hover:bg-blue-50"
          >
            Get Started
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;